/* eslint-disable no-param-reassign */
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../graphql/mutations';
import {
  mealFromSharedMeal,
  newMealWithDefaultFields,
} from '../services/meals';
import { updateFoodBrainDerivedDataOnMeal } from './meal_operations';
import { cloneObject } from './utils';
import { OriginObjectType } from '../API';
import {
  boardWithMealAdded,
  CURRENT_SMORG_BOARD_SCHEMA_VERSION,
  defaultMenuStructureForNewEndUserBoard,
  sharedBoardIDFromBoard,
} from '../services/smorg_board';
import { mealsRemovedFromMealBasket } from '../features/end_user_shopping/end_user_shopping_slice';
import { healthProGroupForSpaceID } from '../services/users';
import { appSyncGraphQLApi } from '../app/smorgAppSyncGraphQLApi';
import { showGlobalSnackbar } from '../features/global_snackbar/global_snackbar_slice';
import { deduplicate } from '../services/arrays';

const client = generateClient();

export const setSharedBoardAccessLevel = async (
  sharedBoard,
  accessLevel,
  updateSharedBoard,
) => {
  const newSharedBoard = { ...sharedBoard, accessLevel };
  const { data: updatedSharedBoard } = await updateSharedBoard(newSharedBoard);
  return updatedSharedBoard;
};

export const expandIngredientsForExclusionOperation = async (
  rootIngredientNames,
  locales,
) => {
  const response = await client.graphql({
    query: mutations.expandIngredientsForExclusion,
    variables: {
      rootIngredientNames,
      locales,
    },
  });
  return response.data.expandIngredientsForExclusion;
};

export const advancedSearchGRCOperation = async (searchCriteria, offset) => {
  const response = await client.graphql({
    query: mutations.advancedSearchGRC,
    variables: { searchCriteria, offset },
  });
  return response.data.advancedSearchGRC;
};

export const advancedSearchOwnMealsOperation = async (
  searchCriteria,
  offset,
) => {
  const response = await client.graphql({
    query: mutations.advancedSearchOwnMeals,
    variables: {
      searchCriteria,
      offset,
    },
  });
  return response.data.advancedSearchOwnMeals;
};

export const getGRCResultsOperation = async (
  spaceID,
  grcRecipeCategoryID,
  offset,
) => {
  const response = await client.graphql({
    query: mutations.getGRCResults,
    variables: {
      spaceID,
      grcRecipeCategoryID,
      offset,
    },
  });
  return response.data.getGRCResults;
};

export const searchIngredientDataOperation = async (searchCriteria, offset) => {
  const response = await client.graphql({
    query: mutations.searchIngredientData,
    variables: {
      searchCriteria,
      offset,
    },
  });
  return response.data.searchIngredientData;
};

export const searchFoodDataOperation = async (searchCriteria, offset) => {
  const response = await client.graphql({
    query: mutations.searchFoodData,
    variables: { searchCriteria, offset },
  });
  return response.data.searchFoodData;
};

export const getFoodDataItemOperation = async (foodDataID) => {
  const response = await client.graphql({
    query: mutations.getFoodDataItem,
    variables: { foodDataID },
  });
  return response.data.getFoodDataItem;
};

export const newMealAddedToRecipesBoard = async (
  recipesBoard,
  laneId,
  mealWithoutId,
  position,
  customerFoodDataID,
  tracker,
  userProfile,
  currentSpaceMembership,
  healthProSpace,
  createMeal,
  updateSmorgBoard,
  updateMeal,
  lazyGetMeal,
  cb,
) => {
  const isImported = !!mealWithoutId.recipes[0]?.recipeUrl;
  const currentHealthProGroup =
    healthProSpace && healthProGroupForSpaceID(healthProSpace.id);
  // const recipesBoard = standaloneRecipesBoardByIdSelector(
  //   getState(),
  //   recipesBoardId,
  // );
  // const isRecipesBoardEmbeddedInProgramme =
  //   recipesBoard?.embeddedInContainerType === ContainerType.PROGRAMME &&
  //   !!recipesBoard?.embeddedInContainerID;
  // const programmeId = recipesBoard.embeddedInContainerID;
  const input = newMealWithDefaultFields(
    mealWithoutId,
    recipesBoard.id,
    currentSpaceMembership?.id,
    currentHealthProGroup,
  );

  const { data: meal } = await createMeal(input);
  // if (isRecipesBoardEmbeddedInProgramme) {
  //   dispatch({
  //     type: 'NEW_MEAL_ADDED_TO_PROGRAMME_EMBEDDED_RECIPES_BOARD',
  //     programmeId,
  //     laneId,
  //     meal,
  //     position,
  //   });
  // } else {
  const newBoard = boardWithMealAdded(recipesBoard, laneId, meal, position);

  // }
  if (cb) {
    cb(meal);
  }
  const counters = ['numMealsAdded'];
  if (isImported) {
    counters.push('numMealsImported');
  } else {
    counters.push('numMealsAddedManually');
  }
  tracker(
    userProfile,
    [
      {
        name: 'Add new meal',
        args: {
          import: isImported,
          url: mealWithoutId.recipes[0]?.recipeUrl,
          section: 'recipes',
        },
      },
    ],
    counters,
  );
  await Promise.all([
    updateFoodBrainDerivedDataOnMeal(
      meal.id,
      meal.recipes[0],
      meal.derivedNutrition?.ingredientNutrition,
      customerFoodDataID,
      lazyGetMeal,
      updateMeal,
    ),
    updateSmorgBoard(newBoard),
  ]);
};
export const copySharedMealToBoard = async (
  sharedMeal,
  toRecipesBoard,
  toMenuId,
  currentSpaceMembership,
  updateSmorgBoard,
  createMeal,
  userProfile,
  tracker,
  healthProSpace,
  cb,
  dispatch,
) => {
  const currentHealthProGroup =
    healthProSpace && healthProGroupForSpaceID(healthProSpace.id);

  const mealWithoutId = mealFromSharedMeal(sharedMeal);

  const input = newMealWithDefaultFields(
    mealWithoutId,
    toRecipesBoard.id,
    currentSpaceMembership?.id,
    currentHealthProGroup,
  );

  const { data: copiedMeal } = await createMeal(input);

  const newBoard = boardWithMealAdded(toRecipesBoard, toMenuId, copiedMeal);
  tracker(userProfile, [
    {
      name: 'Meal copied',
      args: {
        recipeTitle: copiedMeal.recipes[0]?.title,
        id: copiedMeal.id,
      },
    },
  ]);
  dispatch(
    appSyncGraphQLApi.util.updateQueryData(
      'getDistilledMealsByParent',
      toRecipesBoard.id,
      (draft) => {
        draft[copiedMeal.id] = copiedMeal;
      },
    ),
  );
  dispatch(
    appSyncGraphQLApi.util.updateQueryData(
      'getFullMealsByParent',
      toRecipesBoard.id,
      (draft) => {
        draft[copiedMeal.id] = copiedMeal;
      },
    ),
  );
  if (cb) {
    cb(copiedMeal);
  }
  await updateSmorgBoard(newBoard);
};

export const copyMealToBoard = async (
  meal,
  toRecipesBoard,
  toMenuId,
  currentSpaceMembership,
  updateSmorgBoard,
  createMeal,
  userProfile,
  tracker,
  healthProSpace,
  cb,
  dispatch,
  position,
) => {
  const currentHealthProGroup =
    healthProSpace && healthProGroupForSpaceID(healthProSpace.id);

  const mealWithoutId = cloneObject(meal);

  const input = newMealWithDefaultFields(
    mealWithoutId,
    toRecipesBoard.id,
    currentSpaceMembership?.id,
    currentHealthProGroup,
  );

  const { data: copiedMeal } = await createMeal(input);

  const newBoard = boardWithMealAdded(
    toRecipesBoard,
    toMenuId,
    copiedMeal,
    position,
  );
  dispatch(
    appSyncGraphQLApi.util.updateQueryData(
      'getDistilledMealsByParent',
      toRecipesBoard.id,
      (draft) => {
        draft[copiedMeal.id] = copiedMeal;
      },
    ),
  );
  dispatch(
    appSyncGraphQLApi.util.updateQueryData(
      'getFullMealsByParent',
      toRecipesBoard.id,
      (draft) => {
        draft[copiedMeal.id] = copiedMeal;
      },
    ),
  );
  tracker(userProfile, [
    {
      name: 'Meal copied',
      args: {
        recipeTitle: copiedMeal.recipes[0]?.title,
        id: copiedMeal.id,
      },
    },
  ]);
  if (cb) {
    cb(copiedMeal);
  }
  await updateSmorgBoard(newBoard);
};

export const unpublishRecipesBoardFromSpace = async (
  recipesBoard,
  healthProSpace,
  updateSpace,
  dispatch,
) => {
  const publishedSharedBoardID = sharedBoardIDFromBoard(recipesBoard);
  const updatedSpace = {
    ...healthProSpace,
    sharedRecipesBoardIDs: (healthProSpace.sharedRecipesBoardIDs || []).filter(
      (id) => id !== publishedSharedBoardID,
    ),
  };
  await updateSpace(updatedSpace);
  dispatch(
    showGlobalSnackbar({
      notificationText: 'Board was unpublished',
    }),
  );
};

export const removeRecipesBoard = async (
  recipesBoard,
  healthProSpace,
  deleteMeal,
  deleteSmorgBoard,
  updateSpace,
  dispatch,
) => {
  if (healthProSpace) {
    await unpublishRecipesBoardFromSpace(
      recipesBoard,
      healthProSpace,
      updateSpace,
      dispatch,
    );
  }
  const mealIDs = recipesBoard.menus.flatMap((menu) => menu.mealIDs);
  dispatch(mealsRemovedFromMealBasket(mealIDs));
  try {
    await Promise.all(
      mealIDs.map((mealID) =>
        deleteMeal({ id: mealID, parentID: recipesBoard.id }),
      ),
    );
  } catch (e) {
    console.warn(e);
  }
  return deleteSmorgBoard(recipesBoard.id);
};

export const addStandaloneRecipesBoardShareRecord = async (
  recipesBoard,
  sharedBoardID,
  version,
  updatedOn,
  updateSmorgBoard,
) => {
  const newBoard = {
    ...recipesBoard,
    shareRecords: [
      ...(recipesBoard.shareRecords || []),
      {
        sharedBoardID,
        version,
        updatedOn,
      },
    ],
  };
  return updateSmorgBoard(newBoard);
};

export const createSharedBoardFromRecipesBoard = async (
  recipesBoard,
  existingSharedBoardID,
  lazyGetFullMealsByParent,
  lazyGetDistilledSharedMealsByParent,
  createSharedBoard,
  createSharedMeal,
  deleteSharedMeal,
  updateSmorgBoard,
  updateSharedBoard,
) => {
  const sharedBoard = {
    schemaVersion: 1,
    title: recipesBoard.title,
    accessLevel: 'PUBLIC',
    // version: versionTag,
    menus: [],
    shortDescription: recipesBoard.shortDescription,
    coverImageUrl: recipesBoard.coverImageUrl,
    availableInMembershipTierIDs: recipesBoard.availableInMembershipTierIDs,
  };
  let createdSharedBoard;
  if (existingSharedBoardID) {
    sharedBoard.id = existingSharedBoardID;
    const existingSharedMeals = await lazyGetDistilledSharedMealsByParent(
      existingSharedBoardID,
    );
    const existingSharedMealIDs = Object.keys(existingSharedMeals);
    console.log(`Removing ${existingSharedMealIDs.length} shared meals`);
    const promises = existingSharedMealIDs.map((sharedMealID) =>
      deleteSharedMeal({ id: sharedMealID, parentID: existingSharedBoardID }),
    );
    await Promise.all(promises);
    createdSharedBoard = (await updateSharedBoard(sharedBoard)).data;
  } else {
    createdSharedBoard = (await createSharedBoard(sharedBoard)).data;
  }
  const { data: fullMeals } = await lazyGetFullMealsByParent(recipesBoard.id);
  const mealIDs = Object.keys(fullMeals);
  const mealPromises = Object.values(fullMeals).map(async (fullMeal) => {
    const sharedMeal = {
      schemaVersion: 1,
      sharedBoardID: createdSharedBoard.id,
      recipes: fullMeal.recipes,
      origin: {
        originObjectType: OriginObjectType.MEAL,
        originObjectID: fullMeal.id,
      },
      derivedNutrition: fullMeal.derivedNutrition,
    };
    const { data: createdSharedMeal } = await createSharedMeal(sharedMeal);
    return createdSharedMeal;
  });
  const sharedMeals = await Promise.all(mealPromises);
  createdSharedBoard = {
    ...createdSharedBoard,
    menus: recipesBoard.menus.map((menu) => ({
      id: menu.id,
      title: menu.title,
      sharedMealIDs: menu.mealIDs
        .filter((sourceMealID) => mealIDs.includes(sourceMealID))
        .map((sourceMealID) => {
          const index = mealIDs.indexOf(sourceMealID);
          return sharedMeals[index].id;
        }),
    })),
  };
  const { data: sb } = await updateSharedBoard(createdSharedBoard);

  await addStandaloneRecipesBoardShareRecord(
    recipesBoard,
    sb.id,
    '1',
    sb.updatedAt,
    updateSmorgBoard,
  );
  return sb;
};

export const publishRecipesBoardToSpace = async (
  recipesBoard,
  healthProSpace,
  lazyGetFullMealsByParent,
  lazyGetDistilledSharedMealsByParent,
  createSharedBoard,
  createSharedMeal,
  deleteSharedMeal,
  updateSmorgBoard,
  updateSharedBoard,
  updateSpace,
  dispatch,
) => {
  const publishedSharedBoardID = sharedBoardIDFromBoard(recipesBoard);
  const isBoardShared = !!publishedSharedBoardID;
  if (!isBoardShared) {
    const newSharedBoard = await createSharedBoardFromRecipesBoard(
      recipesBoard,
      null,
      lazyGetFullMealsByParent,
      lazyGetDistilledSharedMealsByParent,
      createSharedBoard,
      createSharedMeal,
      deleteSharedMeal,
      updateSmorgBoard,
      updateSharedBoard,
    );
    const updatedSpace = {
      ...healthProSpace,
      sharedRecipesBoardIDs: deduplicate([
        ...(healthProSpace.sharedRecipesBoardIDs || []),
        newSharedBoard.id,
      ]),
    };
    await updateSpace(updatedSpace);
    dispatch(
      showGlobalSnackbar({
        notificationText: 'Board was published',
      }),
    );
  } else {
    await createSharedBoardFromRecipesBoard(
      recipesBoard,
      publishedSharedBoardID,
      lazyGetFullMealsByParent,
      lazyGetDistilledSharedMealsByParent,
      createSharedBoard,
      createSharedMeal,
      deleteSharedMeal,
      updateSmorgBoard,
      updateSharedBoard,
    );
    const updatedSpace = {
      ...healthProSpace,
      sharedRecipesBoardIDs: deduplicate([
        ...(healthProSpace.sharedRecipesBoardIDs || []),
        publishedSharedBoardID,
      ]),
    };
    await updateSpace(updatedSpace);
    dispatch(
      showGlobalSnackbar({
        notificationText: 'Board was published',
      }),
    );
  }
};

export const createEndUserFavouritesRecipesBoard = async (
  currentSpaceMembershipID,
  createSmorgBoard,
) => {
  const blankFavouritesBoard = {
    title: 'My Favourites',
    isMyFavouritesBoard: true,
    schemaVersion: CURRENT_SMORG_BOARD_SCHEMA_VERSION,
    spaceMembershipID: currentSpaceMembershipID,
    menus: defaultMenuStructureForNewEndUserBoard(),
  };
  const { data: newSmorgBoard } = await createSmorgBoard(blankFavouritesBoard);
  return newSmorgBoard;
};
